import { fromThreadMessageLike, type MessageTiming, type ThreadAssistantMessagePart, type ThreadMessage } from '@assistant-ui/react';
import { getUserErrorMessage } from '@/shared/api';
import { isImageContentType } from '@/entities/file';
import { toChartArtifacts, toUiArtifacts } from '@/entities/thread/model/toThreadView';
import type { NativeEvent } from './nativeStream';
import { chartArtifactPart, fileArtifactPart, imageArtifactPart, uiArtifactPart } from './toThreadMessages';

function assistant(
  id: string,
  content: ThreadAssistantMessagePart[],
  running: boolean,
  metadata?: ThreadMessage['metadata'],
  createdAt?: Date,
  errorMessage?: string,
): ThreadMessage {
  const status = errorMessage !== undefined
    ? { type: 'incomplete' as const, reason: 'error' as const, error: errorMessage }
    : running ? { type: 'running' as const } : { type: 'complete' as const, reason: 'stop' as const };
  return fromThreadMessageLike(
    { id, role: 'assistant', content, metadata, createdAt, status },
    id,
    { type: 'complete', reason: 'stop' },
  );
}

function updateAssistant(message: ThreadMessage, content: ThreadAssistantMessagePart[], running: boolean, event: NativeEvent, runStartedAt?: number, errorMessage?: string): ThreadMessage {
  const previous = message.metadata?.timing;
  const streamStartTime = previous?.streamStartTime ?? runStartedAt ?? (Date.parse(event.timestamp) || Date.now());
  const toolCallCount = content.filter((part) => part.type === 'tool-call').length;
  const timing: MessageTiming = running
    ? { ...previous, streamStartTime, totalChunks: (previous?.totalChunks ?? 0) + 1, toolCallCount }
    // Use the client clock for both ends of this measurement (matching
    // WorkingIndicator's `Date.now() - runStartedAt`), never the server
    // event.timestamp for one side only -- mixing a server end-instant with
    // a start-instant of uncertain origin (client send time, or server
    // started_at on reattach) produced a value that didn't match either the
    // live thread-level clock or the persisted server duration shown after
    // a refresh.
    : { ...previous, streamStartTime, totalChunks: (previous?.totalChunks ?? 0) + 1, toolCallCount, totalStreamTime: Math.max(0, Date.now() - streamStartTime) };
  // ActionBar's feedback buttons are gated on metadata.custom.runId (see
  // ActionBar.tsx) -- historical/reattached messages get it from
  // toThreadMessages.ts's persisted record, but a live-streamed message
  // never had it threaded in here despite every NativeEvent carrying its
  // own run_id, so feedback silently never appeared past the first turn.
  const previousCustom = (message.metadata?.custom ?? {}) as Record<string, unknown>;
  const custom = event.run_id ? { ...previousCustom, runId: event.run_id } : previousCustom;
  const metadata = { custom, timing } as ThreadMessage['metadata'];
  return assistant(
    message.id,
    // Parts stay in native emission order. `MessagePrimitive.GroupedParts`
    // keys group nodes by sibling position (`buildGroupTree`: "stable under
    // append-only streaming"), so reordering or filtering an existing part
    // mid-stream re-keys the subtree and remounts every tool card inside it,
    // discarding their local open state. Presentation order belongs in
    // `groupBy`/render, never in this array.
    content,
    running,
    metadata,
    message.content.length === 0 ? new Date(event.timestamp) : message.createdAt,
    errorMessage,
  );
}

function payloadText(payload: Record<string, unknown>): string {
  return typeof payload.text === 'string' ? payload.text : typeof payload.content === 'string' ? payload.content : '';
}

type OrchestrationConsultPayload = { agentId: string; displayName: string; rationale: string };

function payloadConsults(payload: Record<string, unknown>): OrchestrationConsultPayload[] {
  const raw = payload.consults;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c): c is Record<string, unknown> => typeof c === 'object' && c !== null)
    .map((c) => ({
      agentId: typeof c.agent_id === 'string' ? c.agent_id : '',
      displayName: typeof c.display_name === 'string' ? c.display_name : '',
      rationale: typeof c.rationale === 'string' ? c.rationale : '',
    }))
    .filter((c) => c.displayName !== '');
}

type ToolCallPart = Extract<ThreadAssistantMessagePart, { type: 'tool-call' }>;

function isAgentTool(toolName: string): boolean {
  return toolName.startsWith('Agent:');
}

function subAgentAssistant(id: string, content: ThreadAssistantMessagePart[], running: boolean): ThreadMessage {
  return assistant(id, content, running);
}

function subAgentMessages(toolCallId: string, task: unknown): ThreadMessage[] {
  const messages: ThreadMessage[] = [];
  if (typeof task === 'string' && task) {
    messages.push(fromThreadMessageLike(
      { id: `${toolCallId}-prompt`, role: 'user', content: [{ type: 'text', text: task }] },
      `${toolCallId}-prompt`,
      { type: 'complete', reason: 'stop' },
    ));
  }
  messages.push(subAgentAssistant(`${toolCallId}-response`, [], true));
  return messages;
}

function updateSubAgent(part: ToolCallPart, update: (content: ThreadAssistantMessagePart[]) => ThreadAssistantMessagePart[], running: boolean): ToolCallPart {
  const messages = [...(part.messages ?? subAgentMessages(part.toolCallId, part.args.task))];
  const last = messages.at(-1);
  if (last?.role !== 'assistant') return part;
  messages[messages.length - 1] = subAgentAssistant(last.id, update([...last.content] as ThreadAssistantMessagePart[]), running);
  return { ...part, messages };
}

function updateAgentTool(content: ThreadAssistantMessagePart[], parentToolCallId: string, update: (part: ToolCallPart) => ToolCallPart): ThreadAssistantMessagePart[] {
  return content.map((part) => part.type === 'tool-call' && part.toolCallId === parentToolCallId && isAgentTool(part.toolName)
    ? update(part)
    : part) as ThreadAssistantMessagePart[];
}

function appendSubAgentTranscript(part: ToolCallPart, value: unknown): ToolCallPart {
  if (!Array.isArray(value)) return part;
  const messages = [...(part.messages ?? subAgentMessages(part.toolCallId, part.args.task))];
  const seen = new Set(messages.flatMap((message) => (message.content as Array<{ type: string; text?: string }>)
    .filter((item) => item.type === 'text' && typeof item.text === 'string')
    .map((item) => `${message.role}:${item.text}`)));
  value.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object') return;
    const { role, text } = entry as { role?: unknown; text?: unknown };
    if ((role !== 'user' && role !== 'assistant') || typeof text !== 'string' || seen.has(`${role}:${text}`)) return;
    const id = `${part.toolCallId}-transcript-${index}`;
    messages.push(role === 'assistant'
      ? subAgentAssistant(id, [{ type: 'text', text }], false)
      : fromThreadMessageLike({ id, role, content: [{ type: 'text', text }] }, id, { type: 'complete', reason: 'stop' }));
  });
  return { ...part, messages };
}

function promoteNarration(content: ThreadAssistantMessagePart[]): ThreadAssistantMessagePart[] {
  const last = content.at(-1);
  return last?.type === 'text'
    ? [...content.slice(0, -1), { type: 'data', name: 'argus_narration', data: { text: last.text } }]
    : content;
}

function currentAssistant(messages: ThreadMessage[], running: boolean): ThreadMessage {
  const current = messages.at(-1);
  if (current?.role === 'assistant') return current;
  const created = assistant(crypto.randomUUID(), [], running);
  messages.push(created);
  return created;
}

const GENERATIVE_UI_COMPONENTS = new Set(['VegaLiteChart', 'Mermaid', 'Svg']);

// The streaming path opens a tool call with empty `args` and the server's
// fully-parsed args event is persist-only, so the live part only ever has
// `argsText`. Promote it to `args` once it parses, so consumers reading
// `args` (e.g. the artifact panel's `html`) match a reloaded thread.
function appendArgsDelta<T extends { args?: unknown; argsText?: string }>(part: T, delta: string): T {
  const argsText = (part.argsText ?? '') + delta;
  let parsed: unknown;
  try {
    parsed = JSON.parse(argsText);
  } catch {
    return { ...part, argsText };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { ...part, argsText };
  const current = part.args && typeof part.args === 'object' ? part.args as Record<string, unknown> : {};
  return { ...part, argsText, args: { ...current, ...(parsed as Record<string, unknown>) } };
}

function generativeUiParts(payload: Record<string, unknown>): ThreadAssistantMessagePart[] {
  const candidate = payload.ui_artifacts ?? payload.ui_artifact ?? payload.generative_ui ?? payload.artifact ?? payload.result;
  const values = Array.isArray(candidate) ? candidate : candidate ? [candidate] : [];
  return values.flatMap((value): ThreadAssistantMessagePart[] => {
    if (!value || typeof value !== 'object') return [];
    const artifact = value as Record<string, unknown>;
    const inline = artifact.inline;
    const inlineRecord = inline && typeof inline === 'object' ? inline as Record<string, unknown> : null;
    const component = inlineRecord?.component ?? artifact.component;
    const props = inlineRecord?.props ?? artifact.props;
    if (typeof component !== 'string' || !GENERATIVE_UI_COMPONENTS.has(component) || !props || typeof props !== 'object') return [];
    return [{ type: 'generative-ui', spec: { root: [{ component, props: props as Record<string, unknown> }] } }];
  });
}

export function downloadableFilePart(toolName: string, result: unknown): ThreadAssistantMessagePart[] {
  if (toolName !== 'artifactdownload' && !toolName.endsWith('__artifactdownload')) return [];
  // Structured MCP results arrive as a content-block array (`[{type, data}]`
  // or `[{type: 'text', text: '<json string>'}]` -- artifactdownload's actual
  // wire shape) rather than the plain JSON-string shape below; unwrap either
  // block shape and recurse so all three produce the same file part.
  if (Array.isArray(result)) {
    return result.flatMap((part) => {
      if (!part || typeof part !== 'object') return [];
      const value = part as Record<string, unknown>;
      if (value.type === 'json') return downloadableFilePart(toolName, value.data);
      if (value.type === 'text') return downloadableFilePart(toolName, value.text);
      return downloadableFilePart(toolName, value);
    });
  }
  const value = typeof result === 'string'
    ? (() => {
        try { return JSON.parse(result) as unknown; } catch { return null; }
      })()
    : result;
  if (!value || typeof value !== 'object') return [];
  const artifact = value as Record<string, unknown>;
  if (typeof artifact.download_url !== 'string' || typeof artifact.filename !== 'string') return [];
  return [{
    type: 'file',
    data: artifact.download_url,
    filename: artifact.filename,
    mimeType: typeof artifact.content_type === 'string' ? artifact.content_type : undefined,
  } as ThreadAssistantMessagePart];
}

// For failures that happen outside the SSE event stream entirely (the fetch/
// stream connection itself broke and couldn't be recovered) — same
// attach-to-the-message mechanism as a backend-reported `error` event, so
// both cases render identically instead of one being a message-level error
// and the other a toast that's gone by the time anyone looks back.
export function markLastAssistantErrored(messages: readonly ThreadMessage[], errorMessage: string): ThreadMessage[] {
  const next = [...messages];
  const message = currentAssistant(next, false);
  next[next.length - 1] = assistant(message.id, message.content as ThreadAssistantMessagePart[], false, message.metadata, message.createdAt, errorMessage);
  return next;
}

// A thread-level (orchestrator-owned) question pauses the turn: the run is
// waiting on the user, not working. The per-message `status` is what drives
// the "Working…" timer and the "Working on your answer" banner
// (ChatMessage.tsx reads `message.status.type === 'running'`), and it is a
// separate signal from the session-level `isRunning` flag — clearing one does
// not clear the other. Without this the message keeps ticking "Working · Nm"
// while the clarification card waits for an answer, and on reload the reattach
// stream leaves the last replayed event marked running. Downgrade the last
// assistant message to a settled (non-running, non-error) status while the
// pause is open; resume_accepted re-applies live running events afterwards.
//
// If the transcript has no trailing assistant message at all, one settled,
// empty placeholder is appended. That is the reload case: the backend does not
// persist the assistant row while a run is paused (execution.py sets
// `persist_assistant_message=not has_pending_interaction`), so hydrated history
// ends with the user's prompt and the "Waiting for your answer" slot (which
// lives on the assistant message in ChatMessage) would have nowhere to render.
export function markLastAssistantPaused(messages: readonly ThreadMessage[]): ThreadMessage[] {
  const current = messages.at(-1);
  const next = [...messages];
  if (current?.role !== 'assistant') {
    currentAssistant(next, false);
    return next;
  }
  if (current.status?.type !== 'running') return next;
  next[next.length - 1] = assistant(current.id, current.content as ThreadAssistantMessagePart[], false, current.metadata, current.createdAt);
  return next;
}

export function applyNativeEvent(
  messages: readonly ThreadMessage[],
  event: NativeEvent,
  running: boolean,
  runStartedAt?: number,
): ThreadMessage[] {
  const next = [...messages];
  const type = event.event_type;
  const payload = event.payload;
  if (type === 'run_started') {
    // Run state is owned by the external runtime store. Do not materialize an
    // empty assistant message here: assistant-ui would render its action bar
    // before the model has produced any visible content.
  } else if (type === 'text_delta') {
    const message = currentAssistant(next, running);
    const content = [...message.content] as ThreadAssistantMessagePart[];
    const last = content.at(-1);
    if (last?.type === 'text') content[content.length - 1] = { ...last, text: last.text + payloadText(payload) };
    else content.push({ type: 'text', text: payloadText(payload) });
    next[next.length - 1] = updateAssistant(message, content, running, event, runStartedAt);
  } else if (type === 'thinking' || type === 'thinking_delta') {
    const message = currentAssistant(next, running);
    const content = [...message.content] as ThreadAssistantMessagePart[];
    const last = content.at(-1);
    if (last?.type === 'reasoning') content[content.length - 1] = { ...last, text: last.text + payloadText(payload) };
    else content.push({ type: 'reasoning', text: payloadText(payload) });
    next[next.length - 1] = updateAssistant(message, content, running, event, runStartedAt);
  } else if (type === 'orchestration_preamble' || type === 'claude_narration') {
    const message = currentAssistant(next, running);
    const content = [...message.content] as ThreadAssistantMessagePart[];
    const consults = payloadConsults(payload);
    // Consults name a "specialist_selection" part (durable — never blanked at
    // `done`, unlike the flat narration below) so the phase-1 checklist
    // survives past run completion, matching how tool-call cards persist.
    // Runtimes without structured consults (e.g. the generic Claude runtime's
    // own orchestration_preamble usage) fall back to the flat text part.
    if (consults.length > 0) {
      const selection = { type: 'data' as const, name: 'specialist_selection', data: { consults } } as ThreadAssistantMessagePart;
      const existing = content.findIndex((part) => part.type === 'data' && part.name === 'specialist_selection');
      if (existing >= 0) content[existing] = selection;
      else content.push(selection);
    } else {
      const narration = { type: 'data' as const, name: 'claude_narration', data: { text: payloadText(payload) } } as ThreadAssistantMessagePart;
      // Replace in place rather than filter-then-push: removing the previous
      // narration part shifts the index of every part emitted after it, which
      // remounts their group subtree mid-stream.
      const existing = content.findIndex((part) => part.type === 'data' && (part.name === 'claude_narration' || part.name === 'orchestration_preamble'));
      if (existing >= 0) content[existing] = narration;
      else content.push(narration);
    }
    next[next.length - 1] = updateAssistant(message, content, running, event, runStartedAt);
  } else if (type === 'generative_ui' || type === 'ui_artifact' || type === 'artifact_created') {
    const parts = generativeUiParts(payload);
    if (parts.length > 0) {
      const message = currentAssistant(next, running);
      next[next.length - 1] = updateAssistant(message, [...message.content, ...parts] as ThreadAssistantMessagePart[], running, event, runStartedAt);
    }
  } else if (type === 'file_generated') {
    // Argus's `consult`/`build_deck` tools surface a specialist's generated
    // file as its own top-level `file_generated` event (see
    // `_map_orchestration_event`'s `specialist_response` handling) rather
    // than as a downloadable-file tool-call result, so it needs its own
    // branch here -- without it, the artifact silently never reached the
    // live message and only appeared after a reload rehydrated it from
    // `record.artifacts` in toThreadMessages.ts.
    const filename = typeof payload.filename === 'string' ? payload.filename : '';
    if (filename) {
      const artifact = {
        artifactId: typeof payload.artifact_id === 'string' ? payload.artifact_id : '',
        filename,
        contentType: typeof payload.content_type === 'string' ? payload.content_type : undefined,
        sizeBytes: typeof payload.size_bytes === 'number' ? payload.size_bytes : undefined,
        downloadUrl: typeof payload.download_url === 'string' ? payload.download_url : undefined,
      };
      const part = isImageContentType(artifact.contentType) ? imageArtifactPart(artifact) : fileArtifactPart(artifact);
      const message = currentAssistant(next, running);
      next[next.length - 1] = updateAssistant(message, [...message.content, part] as ThreadAssistantMessagePart[], running, event, runStartedAt);
    }
  } else if (type === 'tool_call_start') {
    const message = currentAssistant(next, running);
    const toolCallId = String(payload.tool_call_id ?? event.event_id);
    const toolName = String(payload.tool_name ?? payload.name ?? event.source);
    const args = (payload.args && typeof payload.args === 'object' ? payload.args : {}) as Record<string, unknown>;
    const argsText = typeof payload.args_text === 'string'
      ? payload.args_text
      : Object.keys(args).length > 0 ? JSON.stringify(args) : '';
    const parentToolCallId = typeof payload.parent_tool_call_id === 'string' ? payload.parent_tool_call_id : null;
    if (parentToolCallId) {
      const content = updateAgentTool([...message.content] as ThreadAssistantMessagePart[], parentToolCallId, (part) => updateSubAgent(part, (nested) => {
        const toolCall = { type: 'tool-call' as const, toolCallId, toolName, args, argsText } as ThreadAssistantMessagePart;
        const index = nested.findIndex((candidate) => candidate.type === 'tool-call' && candidate.toolCallId === toolCallId);
        return index < 0 ? [...nested, toolCall] : nested.map((candidate, nestedIndex) =>
          nestedIndex === index ? { ...candidate, ...toolCall } as ThreadAssistantMessagePart : candidate);
      }, true));
      next[next.length - 1] = updateAssistant(message, content, running, event, runStartedAt);
      return next;
    }
    const existingIndex = message.content.findIndex((part) => part.type === 'tool-call' && part.toolCallId === toolCallId);
    const content = isAgentTool(toolName)
      ? promoteNarration([...message.content] as ThreadAssistantMessagePart[])
      : [...message.content] as ThreadAssistantMessagePart[];
    // Mirrors event_collector.py's `_args_with_argus_attribution`, which only
    // runs server-side when persisting -- without this, a live-streamed card
    // has no agent_id in `args` at all (only a reloaded thread would), so
    // anything resolving a specialist by agent_id (e.g. citation links) would
    // silently fail during the live stream.
    const agentArgs = isAgentTool(toolName) && typeof payload.agent_id === 'string'
      ? { ...args, _argus: { ...(args._argus as Record<string, unknown> | undefined), delegated_agent_id: payload.agent_id } }
      : args;
    const toolCall = {
      type: 'tool-call' as const, toolCallId, toolName, args: agentArgs, argsText,
      ...(isAgentTool(toolName) ? { messages: subAgentMessages(toolCallId, args.task) } : {}),
    } as ThreadAssistantMessagePart;
    if (existingIndex >= 0) content[existingIndex] = { ...content[existingIndex], ...toolCall } as ThreadAssistantMessagePart;
    else content.push(toolCall);
    next[next.length - 1] = updateAssistant(message, content, running, event, runStartedAt);
  } else if (type === 'skill_use') {
    const message = currentAssistant(next, running);
    const content = [...message.content] as ThreadAssistantMessagePart[];
    const toolCallId = String(payload.tool_call_id ?? event.event_id);
    const args = (payload.args && typeof payload.args === 'object' ? payload.args : {
      skill: payload.skill_name ?? payload.skillName ?? 'unknown',
    }) as Record<string, unknown>;
    const toolCall = {
      type: 'tool-call' as const,
      toolCallId,
      toolName: 'Skill',
      args,
      argsText: JSON.stringify(args),
    } as ThreadAssistantMessagePart;
    const existingIndex = content.findIndex((part) => part.type === 'tool-call' && part.toolCallId === toolCallId);
    if (existingIndex >= 0) content[existingIndex] = { ...content[existingIndex], ...toolCall } as ThreadAssistantMessagePart;
    else content.push(toolCall);
    next[next.length - 1] = updateAssistant(message, content, running, event, runStartedAt);
  } else if (type === 'tool_call_args_delta') {
    const message = currentAssistant(next, running);
    const id = String(payload.tool_call_id ?? '');
    const delta = String(payload.delta ?? payload.text ?? payload.args ?? '');
    const parentToolCallId = typeof payload.parent_tool_call_id === 'string' ? payload.parent_tool_call_id : null;
    const content = parentToolCallId
      ? updateAgentTool([...message.content] as ThreadAssistantMessagePart[], parentToolCallId, (part) => updateSubAgent(part, (nested) => nested.map((candidate) => candidate.type === 'tool-call' && candidate.toolCallId === id
        ? appendArgsDelta(candidate, delta)
        : candidate) as ThreadAssistantMessagePart[], true))
      : message.content.map((part) => part.type === 'tool-call' && part.toolCallId === id
      ? appendArgsDelta(part, delta)
      : part) as ThreadAssistantMessagePart[];
    next[next.length - 1] = updateAssistant(message, content, running, event, runStartedAt);
  } else if (type === 'tool_call_end') {
    const message = currentAssistant(next, running);
    const id = String(payload.tool_call_id ?? '');
    const result = payload.result ?? payload.content ?? '';
    // MCP tool results sometimes arrive as a structured content-block array
    // instead of the plain string/JSON `result` shape -- prefer it when present.
    const artifactResult = payload.result_content ?? result;
    const parentToolCallId = typeof payload.parent_tool_call_id === 'string' ? payload.parent_tool_call_id : null;
    const content = parentToolCallId
      ? updateAgentTool([...message.content] as ThreadAssistantMessagePart[], parentToolCallId, (part) => updateSubAgent(part, (nested) => nested.map((candidate) => candidate.type === 'tool-call' && candidate.toolCallId === id
        ? { ...candidate, result, isError: Boolean(payload.error) }
        : candidate) as ThreadAssistantMessagePart[], true))
      : message.content.map((part) => part.type === 'tool-call' && part.toolCallId === id
      ? { ...part, result, isError: Boolean(payload.error) }
      : part) as ThreadAssistantMessagePart[];
    // Attach downloadable-file/generative-ui parts as siblings of whichever
    // tool-call actually just finished. A nested specialist call's own
    // tool-call object only ever lives inside that specialist's own
    // sub-message content -- looking it up in the top-level array (as this
    // used to) always missed it, silently dropping every specialist-produced
    // download link when a specialist was invoked through Argus (#751).
    const appendResultParts = (list: ThreadAssistantMessagePart[], toolName: string): ThreadAssistantMessagePart[] => {
      const withResults = [...list];
      const fileParts = downloadableFilePart(toolName, artifactResult);
      for (const filePart of fileParts) {
        const duplicate = filePart.type === 'file' && withResults.some((part) => part.type === 'file' && part.data === filePart.data);
        if (!duplicate) withResults.push(filePart);
      }
      const generatedParts = generativeUiParts({ result });
      for (const generatedPart of generatedParts) {
        const duplicate = withResults.some((part) => part.type === 'generative-ui' && JSON.stringify(part) === JSON.stringify(generatedPart));
        if (!duplicate) withResults.push(generatedPart);
      }
      return withResults;
    };
    const attachResultParts = (list: ThreadAssistantMessagePart[]): ThreadAssistantMessagePart[] => {
      const activeTool = list.find((part): part is ToolCallPart => part.type === 'tool-call' && part.toolCallId === id);
      return activeTool ? appendResultParts(list, activeTool.toolName) : list;
    };
    const finalized = parentToolCallId
      ? (() => {
          const nestedUpdated = updateAgentTool(content, parentToolCallId, (part) => updateSubAgent(part, attachResultParts, true));
          // A specialist's generated file must also surface on Argus's own
          // top-level message, not only inside that specialist's card --
          // the card can be (and usually is) collapsed by default, hiding
          // the only working download link from the user entirely.
          const parent = content.find((part): part is ToolCallPart => part.type === 'tool-call' && part.toolCallId === parentToolCallId);
          const nestedContent = (parent?.messages?.at(-1)?.content ?? []) as ThreadAssistantMessagePart[];
          const activeTool = nestedContent.find((part): part is ToolCallPart => part.type === 'tool-call' && part.toolCallId === id);
          return activeTool ? appendResultParts(nestedUpdated, activeTool.toolName) : nestedUpdated;
        })()
      : attachResultParts(updateAgentTool(content, id, (part) => appendSubAgentTranscript(updateSubAgent(part, (nested) => {
        const finalText = typeof payload.result_text === 'string' ? payload.result_text : typeof result === 'string' ? result : '';
        const lastText = nested.filter((item) => item.type === 'text').map((item) => item.text).join('');
        return !finalText || lastText.includes(finalText) ? nested : [...nested, { type: 'text', text: finalText }];
      }, false), payload.sub_agent_messages)));
    next[next.length - 1] = updateAssistant(message, finalized, running, event, runStartedAt);
  } else if (type === 'metadata' && typeof payload.connect_required === 'string') {
    // Run-start preflight: a required-connection remote MCP (e.g. honeycomb)
    // the user hasn't connected. It fails at tool-list time — no tool call to
    // tag — so the backend emits this up front. Surface it as a data part the
    // thread-level ConnectProviderBanner renders (dedup by provider).
    const provider = payload.connect_required;
    const message = currentAssistant(next, running);
    const content = [...message.content] as ThreadAssistantMessagePart[];
    const exists = content.some((part) => part.type === 'data' && part.name === 'connect_required'
      && (part.data as { provider?: string }).provider === provider);
    const updated = exists
      ? content
      : [...content, { type: 'data' as const, name: 'connect_required', data: { provider } } as ThreadAssistantMessagePart];
    next[next.length - 1] = updateAssistant(message, updated, running, event, runStartedAt);
  } else if (type === 'metadata' && payload.argus_execution && typeof payload.argus_execution === 'object') {
    const execution = payload.argus_execution as Record<string, unknown>;
    const toolCallId = typeof execution.tool_call_id === 'string' ? execution.tool_call_id : '';
    const channel = execution.channel === 'reasoning' ? 'reasoning' : execution.channel === 'text' ? 'text' : null;
    const text = typeof execution.text === 'string' ? execution.text : '';
    if (toolCallId && channel && text) {
      const message = currentAssistant(next, running);
      const content = updateAgentTool([...message.content] as ThreadAssistantMessagePart[], toolCallId, (part) => updateSubAgent(part, (nested) => {
        const last = nested.at(-1);
        return last?.type === channel
          ? [...nested.slice(0, -1), { ...last, text: last.text + text }]
          : [...nested, { type: channel, text }];
      }, true));
      next[next.length - 1] = updateAssistant(message, content, running, event, runStartedAt);
    }
  } else if (type === 'user_question' || type === 'approval_requested') {
    // Only specialist questions reach here — the orchestrator's own go to the
    // thread-global slot in `useNativeAgenthubExternalRuntime`.
    const parentToolCallId = typeof payload.parent_tool_call_id === 'string' ? payload.parent_tool_call_id : '';
    if (!parentToolCallId) return next;
    const message = currentAssistant(next, running);
    const content = updateAgentTool([...message.content] as ThreadAssistantMessagePart[], parentToolCallId, (part) => updateSubAgent(part, (nested) => {
      const question = {
        type: 'data' as const,
        name: 'specialist_question',
        data: { eventId: event.event_id, reason: type, payload },
      } as ThreadAssistantMessagePart;
      const index = nested.findIndex((candidate) => candidate.type === 'data'
        && candidate.name === 'specialist_question'
        && (candidate.data as { eventId?: string }).eventId === event.event_id);
      return index < 0 ? [...nested, question] : nested;
    }, true));
    next[next.length - 1] = updateAssistant(message, content, running, event, runStartedAt);
  } else if (type === 'resume_accepted') {
    // The answered question is gone server-side; drop the prompt from every
    // card so an answered specialist stops asking. Blanked in place rather
    // than removed, to keep sibling part indices — and therefore open tool
    // cards — stable, the same reason the `done` handler blanks narration.
    const message = next.at(-1);
    if (message?.role === 'assistant') {
      const answered = typeof payload.event_id === 'string' ? payload.event_id : null;
      next[next.length - 1] = updateAssistant(message, message.content.map((part) => part.type === 'tool-call' && isAgentTool(part.toolName) && part.messages
        ? {
          ...part,
          messages: part.messages.map((nestedMessage) => nestedMessage.role !== 'assistant' ? nestedMessage : subAgentAssistant(
            nestedMessage.id,
            (nestedMessage.content as ThreadAssistantMessagePart[]).map((item) => item.type === 'data'
              && item.name === 'specialist_question'
              && (!answered || (item.data as { eventId?: string }).eventId === answered)
              ? { ...item, data: { ...(item.data as object), answered: true } }
              : item),
            true,
          )),
        }
        : part) as ThreadAssistantMessagePart[], running, event, runStartedAt);
    }
  } else if (type === 'done') {
    const message = next.at(-1);
    if (message?.role === 'assistant') {
      // Narration ("Delegating to X...") is a live indicator; once the run is
      // done the response text speaks for the turn. Blank it in place instead
      // of dropping the part — removing it would shift every later part's index
      // and remount their group subtree (collapsing open tool cards) at the
      // exact moment the user starts reading. `NarrationData` renders nothing
      // for empty text. `argus_narration` is a durable record of the
      // orchestrator's journey, not an indicator, so it is left alone.
      let content = message.content.map((part) => part.type === 'data'
        && (part.name === 'claude_narration' || part.name === 'orchestration_preamble')
        ? { ...part, data: { text: '' } }
        : part) as ThreadAssistantMessagePart[];
      // The live stream only ever relayed raw text_delta chunks — chart/
      // vega-lite/mermaid/svg fenced blocks are stripped server-side only
      // once the full response is available (finalize_turn runs
      // parse_chart_artifacts/parse_ui_artifacts after streaming ends), so
      // the bubble is still showing the raw fenced text at this point. When
      // that happened, `done`'s payload carries the same already-cleaned
      // `content` and parsed artifacts a refresh would hydrate from — apply
      // them now so the live bubble matches without a refetch (see #778's
      // investigation of live/persisted render divergence).
      const chartArtifacts = toChartArtifacts(payload.chart_artifacts);
      const uiArtifacts = toUiArtifacts(payload.ui_artifacts);
      if ((chartArtifacts || uiArtifacts) && typeof payload.content === 'string') {
        const cleanedText = payload.content;
        let replacedText = false;
        content = content.map((part) => {
          if (part.type !== 'text') return part;
          if (replacedText) return { ...part, text: '' };
          replacedText = true;
          return { ...part, text: cleanedText };
        });
        if (!replacedText) content = [...content, { type: 'text', text: cleanedText }];
        content = [
          ...content,
          ...(chartArtifacts ?? []).map(chartArtifactPart),
          ...(uiArtifacts ?? []).map(uiArtifactPart),
        ] as ThreadAssistantMessagePart[];
      }
      next[next.length - 1] = updateAssistant(message, content, false, event, runStartedAt);
    }
  } else if (type === 'error') {
    // Backend-reported run failure. Preserve whatever content already
    // streamed in (partial text, tool calls) and attach the error to this
    // message instead of just marking it done — otherwise a failed run
    // looked identical to a successful one except for a toast that had
    // already disappeared by the time anyone looked back at the thread.
    const message = currentAssistant(next, false);
    // payload.message is raw backend/protocol text (e.g. an internal
    // "ClaudeRuntime requires ..." string) — never surface it directly.
    // getUserErrorMessage funnels it through the same curated-fallback path
    // as every other error site.
    const errorMessage = getUserErrorMessage(payload.message);
    next[next.length - 1] = updateAssistant(message, [...message.content] as ThreadAssistantMessagePart[], false, event, runStartedAt, errorMessage);
  }
  return next;
}
